import React from 'react';
import { TFunction } from 'react-i18next';
import i18next from 'i18next';
import { ISettlementTransaction } from '../../models/ISettlementData';
import { CurrencyHelper } from '../../helpers/CurrencyHelper';
import { WebCache } from '../../services/WebCache';
import { Option } from '../../models/Option';
import './DetailPaymentReport.scss';

type Props = {
    transaction: ISettlementTransaction;
    t: TFunction;
};

const flagToYesNo = (flag: boolean) => flag ? i18next.t('yes') : i18next.t('no');

const DetailPaymentTransactionReport: React.FC<Props> = ({ transaction, t }) => {
    const currencySymbol = CurrencyHelper.getCurrencySymbol(transaction.currency);

    const formatCurrency = (value?: number) => {
        if (value === undefined || value === null) return '-';
        return Intl.NumberFormat('es-UY', { style: 'currency', currency: currencySymbol }).format(value);
    };

    const formatPercentage = (value?: number) => {
        if (value === undefined || value === null) return '-';
        return `${value}%`;
    };

    const getQuotasLabel = (): string => {
        if (transaction.quotas == null || transaction.quotas == 0 || transaction.quotas == 1)
            return transaction.quotas + "";
        if ((transaction.currentQuota == 0 || transaction.currentQuota == 1) && (transaction.planQuotaType + "") != "1")
            return transaction.quotas + "";
        return transaction.currentQuota + "/" + transaction.quotas;
    };

    const getMovementTypeLabel = (): string => {
        if (transaction.movementType === 5) return transaction.reverseCode;
        const movementTypes: Array<Option> = i18next.t<Array<Option>>("movementTypes", { returnObjects: true });
        const movementTypeOption = movementTypes.find(option => Number(option.value) === transaction.movementType);
        return movementTypeOption?.label || '';
    };

    const merchantDocumentLabel = WebCache.isUserUYU() ? t('merchantDocument') : t('merchantDocumentArg');

    return (
        <div className="detail-payment-report">
            <div className="dpr-details card">
                <div className="dpr-grid">
                    <div className="dpr-col">
                        <div className="dpr-row">
                            <span className="dpr-label">{t('settlementNumber')}</span>
                            <span className="dpr-value">{transaction.settlementNumber || '-'}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{merchantDocumentLabel}</span>
                            <span className="dpr-value">{(transaction as any).merchantDocument || '-'}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('quotas')}</span>
                            <span className="dpr-value">{getQuotasLabel()}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('planQuoteType')}</span>
                            <span className="dpr-value">{transaction.planQuotaType || '-'}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('movementType')}</span>
                            <span className="dpr-value">{getMovementTypeLabel()}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('isQuotaPromotion')}</span>
                            <span className="dpr-value">{flagToYesNo(transaction.isQuotaPromotion)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('settlementAuthorizationCode')}</span>
                            <span className="dpr-value">{transaction.authorizationCode || '-'}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('tariffPercentage')}</span>
                            <span className="dpr-value">{formatPercentage(transaction.tariffPercentage)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('transactionTariffTaxAmount')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.transactionTariffTaxAmount)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('transactionTaxTariffTaxAmount')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.transactionTaxTariffTaxAmount)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('planQuotaRate')}</span>
                            <span className="dpr-value">{transaction.planQuotaRate ?? '-'}</span>
                        </div>
                    </div>
                    <div className="dpr-col">
                        <div className="dpr-row">
                            <span className="dpr-label">{t('merchantNumber')}</span>
                            <span className="dpr-value">{(transaction as any).merchantNumber || '-'}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('financialCostAmount')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.financialCostAmount)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('taxCostAmount')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.financialCostTaxAmount)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('alícuotaIVAFO')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.alícuotaIVAFO)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('directRatePercentage')}</span>
                            <span className="dpr-value">{formatPercentage(transaction.directRatePercentage)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('directRateAmount')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.directRateAmount)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('directRateTax')}</span>
                            <span className="dpr-value">{formatCurrency(transaction.directRateTaxAmount)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('isCampain')}</span>
                            <span className="dpr-value">{flagToYesNo(transaction.isCampain)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('isCashback')}</span>
                            <span className="dpr-value">{flagToYesNo(transaction.isCashback)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('isVal')}</span>
                            <span className="dpr-value">{flagToYesNo(transaction.vtoDate !== transaction.originalVtoDate)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('finAgp')}</span>
                            <span className="dpr-value">{formatCurrency((transaction as any).finAgp)}</span>
                        </div>
                        <div className="dpr-row">
                            <span className="dpr-label">{t('invoiceNumber')}</span>
                            <span className="dpr-value">{transaction.invoiceNumber || '-'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailPaymentTransactionReport;
